import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api-mysql';
import getImageUrl from '../utils/imageUrl';

const Home = () => {
  const courseOptions = [
    'Project Management Professional (PMP)®',
    'Certified Associate in Project Management (CAPM)®',
    'Program Management Professional (PgMP)®',
    'Portfolio Management Professional (PfMP)®',
    'PMI Agile Certified Practitioner (PMI-ACP)®',
    'PMI Risk Management Professional (PMI-RMP)®',
    'PMI Professional in Business Analysis (PMI-PBA)®'
  ];
  const [achievers, setAchievers] = useState([]);
  const [filteredAchievers, setFilteredAchievers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getAchieverDateValue = (achiever) => {
    if (achiever.date) {
      return achiever.date.split('T')[0];
    }

    const monthIndex = new Date(`${achiever.month} 1, ${achiever.year}`).getMonth();
    return new Date(achiever.year, monthIndex, 1).toISOString().split('T')[0];
  };

  const formatAchieverDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  useEffect(() => {
    fetchAchievers();
  }, []);

  useEffect(() => {
    filterAchievers();
  }, [achievers, searchTerm, selectedYear, selectedMonth, companyFilter, selectedCourse]);

  const fetchAchievers = async () => {
    try {
      console.log('Fetching achievers...');
      const response = await api.get('/achievers');
      console.log('Achievers response:', response.data);
      
      // Sort by date (newest first)
      const sorted = response.data.sort((a, b) => {
        const dateA = getAchieverDateValue(a);
        const dateB = getAchieverDateValue(b);
        return dateB.localeCompare(dateA);
      });
      
      console.log('Setting achievers:', sorted);
      setAchievers(sorted);
      setError(null);
    } catch (error) {
      console.error('Error fetching achievers:', error);
      setError('Failed to fetch achievers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterAchievers = () => {
    let filtered = achievers.filter(achiever => {
      const matchesSearch = !searchTerm || 
        achiever.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        achiever.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        achiever.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (achiever.course || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesYear = !selectedYear || achiever.year.toString() === selectedYear;
      const matchesMonth = !selectedMonth || achiever.month === selectedMonth;
      const matchesCompany = !companyFilter || 
        achiever.company.toLowerCase().includes(companyFilter.toLowerCase());
      const matchesCourse = !selectedCourse || achiever.course === selectedCourse;
      
      return matchesSearch && matchesYear && matchesMonth && matchesCompany && matchesCourse;
    });

    setFilteredAchievers(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedYear('');
    setSelectedMonth('');
    setCompanyFilter('');
    setSelectedCourse('');
  };

  const getYears = () => {
    return [...new Set(achievers.map(a => a.year))].sort((a, b) => b - a);
  };

  const createAchieverCard = (achiever) => {
    const formattedDate = formatAchieverDate(getAchieverDateValue(achiever));
    
    return (
      <div className="col-md-4 col-lg-3" key={achiever.id}>
        <div className="card h-100 achiever-card shadow-sm border-0 bg-white rounded-4 overflow-hidden">
          <img src={getImageUrl(achiever.image)} alt={achiever.name} className="card-img-top" style={{height: '300px', objectFit: 'cover', width: '100%'}} />
          <div className="card-body text-center p-3">
            <h5 className="card-title fw-bold text-dark mb-2" style={{fontSize: '1.3rem'}}>{achiever.name}</h5>
            <p className="text-muted mb-1" style={{fontSize: '1.3rem'}}>{achiever.company}</p>
            <p className="text-primary fw-semibold mb-2" style={{fontSize: '1.25rem'}}>{achiever.role}</p>
            <p className="text-dark mb-2" style={{fontSize: '0.95rem'}}>{achiever.course}</p>
            <div className="mb-2">
              <span className="badge bg-success" style={{fontSize: '0.85rem'}}>
                <i className="bi bi-calendar-check me-1"></i>
                {formattedDate}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <i className="bi bi-arrow-clockwise"></i>
        <div className="mt-3 text-white">Loading achievers...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container text-center mt-5">
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Error:</strong> {error}
          <button className="btn btn-outline-danger ms-3" onClick={() => setError(null)}>
            <i className="bi bi-arrow-clockwise me-1"></i>Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <div className="hero-section">
        <div className="container">
          <div className="mb-4 d-flex justify-content-center align-items-center gap-4">
            <img src="/images/infocareer-logo.png" alt="Infocareer" style={{height: '80px'}} />
            <img src="/images/Proud_PMP_png.png" alt="Proud PMPs" style={{height: '100px'}} />
          </div>
          <h1 className="display-4 fw-bold mb-3">
            <i className="bi bi-trophy-fill me-3"></i>Wall of PMI Certified Professionals
          </h1>
          <p className="lead mb-3">Meet the professionals who successfully earned PMI certifications. <span className="text-warning fw-bold">Your NAME could be next.</span></p>
          <div className="d-flex justify-content-center gap-3">
             <a href="https://infocareerindia.com/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light btn-lg">
              <i className="bi bi-journal-text me-2"></i>Explore Infocareer
            </a>
            <a href="https://pmtribe.infocareerindia.com/" target="_blank" rel="noopener noreferrer" className="btn btn-light btn-lg">
              <i className="bi bi-briefcase-fill me-2"></i>PM Careers Hub
            </a>
            <a href="https://www.infocareerindia.com/blogs-mentoring/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light btn-lg">
              <i className="bi bi-journal-text me-2"></i>Knowledge Hub
            </a>
            <a href="https://elearning.roadtoprojectsuccess.com/web/lite/events/69980dcf8f19a1db876e1373" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light btn-lg">
              <i className="bi bi-calendar-check me-2"></i>Mentor Guidance
            </a>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="container">
        <div className="filter-section">
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label fw-semibold">
                <i className="bi bi-search me-2"></i>Search Achievers
              </label>
              <input 
                type="text" 
                className="form-control" 
                placeholder="Search by name, company, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">
                <i className="bi bi-calendar3 me-2"></i>Year
              </label>
              <select 
                className="form-select" 
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="">All Years</option>
                {getYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">
                <i className="bi bi-calendar-month me-2"></i>Month
              </label>
              <select 
                className="form-select" 
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">All Months</option>
                <option value="January">January</option>
                <option value="February">February</option>
                <option value="March">March</option>
                <option value="April">April</option>
                <option value="May">May</option>
                <option value="June">June</option>
                <option value="July">July</option>
                <option value="August">August</option>
                <option value="September">September</option>
                <option value="October">October</option>
                <option value="November">November</option>
                <option value="December">December</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">
                <i className="bi bi-building me-2"></i>Company
              </label>
              <input 
                type="text" 
                className="form-control" 
                placeholder="Search company..."
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">
                <i className="bi bi-award me-2"></i>Course
              </label>
              <select
                className="form-select"
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
              >
                <option value="">All Courses</option>
                {courseOptions.map(course => (
                  <option key={course} value={course}>{course}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">&nbsp;</label>
              <div className="d-grid">
                <button className="btn btn-outline-primary" onClick={clearFilters}>
                  <i className="bi bi-arrow-clockwise me-1"></i>Clear Filters
                </button>
              </div>
            </div>
          </div>
          
          <div className="mt-3">
            <small>
              <span className="fw-bold text-primary" style={{fontSize: '1.1rem'}}>{filteredAchievers.length} Total Achievers</span>
            </small>
          </div>
        </div>
      </div>

      {/* Results Grid */}
      <div className="container">
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4">
          {filteredAchievers.slice(0, 4).map(achiever => createAchieverCard(achiever))}
        </div>
        
        {/* View Gallery Button */}
        <div className="text-center mt-4">
          <Link to="/gallery" className="btn btn-primary btn-lg">
            <i className="bi bi-images me-2"></i>View Complete Gallery
          </Link>
          <p className="text-muted mt-2">
            <small>Browse all achievers organized by year</small>
          </p>
        </div>
      </div>
    </>
  );
};

export default Home;
